import { useEffect, useState } from 'react';
import { isArray, isNil } from 'lodash';
// Import Types
import type { VisualizationButtonAction } from 'types';
// Import Services
import { CustomerJourneyHttpService } from 'services/http';
// Import Hooks
import useRequest from './UseRequest';

interface UseCJVisualizationButtonActionsResult {
    actions: VisualizationButtonAction[];
    isLoading: boolean;
}

const actionsCache = new Map<string, VisualizationButtonAction[]>();
const inFlightRequests = new Map<string, Promise<VisualizationButtonAction[]>>();

const getCacheKey = (pointInTime: Nilable<string>): string => pointInTime ?? '';

const useCJVisualizationButtonActions = (
    pointInTime: Nilable<string> = null,
): UseCJVisualizationButtonActionsResult => {
    const { doGetRequest } = useRequest();
    const cacheKey = getCacheKey(pointInTime);

    const [actions, setActions] = useState<VisualizationButtonAction[]>(() => actionsCache.get(cacheKey) ?? []);
    const [isLoading, setIsLoading] = useState(() => !actionsCache.has(cacheKey));

    useEffect(() => {
        const cachedActions = actionsCache.get(cacheKey);
        if (!isNil(cachedActions)) {
            setActions(cachedActions);
            setIsLoading(false);
            return;
        }

        let isActive = true;
        setIsLoading(true);

        let actionsRequest = inFlightRequests.get(cacheKey);
        if (isNil(actionsRequest)) {
            const request = CustomerJourneyHttpService.getVisualizationButtonActions();
            actionsRequest = doGetRequest(request.request, { queryString: { pointInTime } })
                .then((response) => {
                    const isSucceeded = response?.HasError !== true && response?.IsCanceled !== true;
                    const data = isSucceeded && isArray(response?.Data) ? response.Data : [];
                    if (isSucceeded) {
                        actionsCache.set(cacheKey, data);
                    }
                    return data;
                })
                .finally(() => {
                    inFlightRequests.delete(cacheKey);
                });
            inFlightRequests.set(cacheKey, actionsRequest);
        }

        actionsRequest.then((data) => {
            if (isActive) {
                setActions(data);
                setIsLoading(false);
            }
        });

        return () => {
            isActive = false;
        };
        // DEPS: [doGetRequest, pointInTime]
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cacheKey]);

    return { actions, isLoading };
};

export default useCJVisualizationButtonActions;
